"use client";

import { useEffect, useState } from "react";
import { useCategoryStore } from "@/stores/categoryStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ModalViewCategoryProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string | null;
}

export default function ModalViewCategory({
  isOpen,
  onClose,
  categoryId,
}: ModalViewCategoryProps) {
  const { fetchCategoryById } = useCategoryStore();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (isOpen && categoryId) {
        setLoading(true);
        const res = await fetchCategoryById(categoryId);
        setData(res);
        setLoading(false);
      }
    };
    run();
  }, [isOpen, categoryId, fetchCategoryById]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Chi tiết danh mục
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Đang tải...</span>
          </div>
        ) : !data ? (
          <div className="p-6 text-center text-gray-600">
            Không tìm thấy danh mục
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Tên danh mục
              </div>
              <div className="text-base font-medium text-gray-900 dark:text-white">
                {data.name}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Slug
              </div>
              <div className="text-base font-medium text-gray-900 dark:text-white">
                {data.slug}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Danh mục cha
              </div>
              <div className="text-base font-medium text-gray-900 dark:text-white">
                {data.parent?.name || "-"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Vị trí
              </div>
              <div className="text-base font-medium text-gray-900 dark:text-white">
                {data.level === 0
                  ? "Gốc"
                  : data.level === 1
                  ? "Cấp 1"
                  : "Cấp 2"}
              </div>
            </div>
            {data.description && (
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Mô tả
                </div>
                <div className="text-base text-gray-900 dark:text-white">
                  {data.description}
                </div>
              </div>
            )}

            {data.canonicalUrl && (
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Canonical URL
                </div>
                <a
                  href={data.canonicalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline underline-offset-2 break-all"
                >
                  {data.canonicalUrl}
                </a>
              </div>
            )}

            {(data.keyword || data.seoTitle || data.seoDescription) && (
              <div className="grid grid-cols-1 gap-4">
                {data.keyword && (
                  <div>
                    <div className="text-sm text-gray-500">Keyword</div>
                    <div className="text-base text-gray-900">
                      {data.keyword}
                    </div>
                  </div>
                )}
                {data.seoTitle && (
                  <div>
                    <div className="text-sm text-gray-500">SEO Title</div>
                    <div className="text-base text-gray-900">
                      {data.seoTitle}
                    </div>
                  </div>
                )}
                {data.seoDescription && (
                  <div>
                    <div className="text-sm text-gray-500">SEO Description</div>
                    <div className="text-base text-gray-900 whitespace-pre-line">
                      {data.seoDescription}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
